import axiosInstance, { extractErrorMessage } from './axiosConfig';
import { UserCreate, UserStatusUpdate, UserListFilter, 
         PaginatedUserResponse, UserApiResponse, TokenUsageApiResponse } from '../types/user';

/**
 * Gets the current authenticated user's information.
 * 
 * @returns Promise with current user data
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await getUserProfile();
 *   console.log('Current user:', response.data);
 * } catch (error) {
 *   console.error('Failed to fetch current user:', error);
 * }
 * ```
 */

export const getUserProfile = async () => {
  try {
    // Add no-cache headers to prevent browser caching of user data
    const response = await axiosInstance.get('/users/me', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar perfil do usuário'
      )
    );
  }
};


/**
 * Fetches all users with pagination, search, and filtering support.
 * Admin access required.
 * 
 * @param options - Filtering and pagination options
 * @returns Promise with paginated array of users
 * 
 * @example
 * ```typescript
 * try {
 *   const users = await getAllUsers({
 *     skip: 0,
 *     limit: 50,
 *     search: 'john',
 *     include_inactive: false
 *   });
 *   console.log('Users:', users.data);
 * } catch (error) {
 *   console.error('Failed to fetch users:', error);
 * }
 * ```
 */
export const getAllUsers = async (options: UserListFilter = {}): Promise<PaginatedUserResponse> => {
  try {
    const params: Record<string, unknown> = {
      skip: options.skip || 0,
      limit: options.limit || 100,
      include_inactive: options.include_inactive || false
    };
    
    if (options.search) {
      params.search = options.search;
    }
    
    const response = await axiosInstance.get('/users/all', { params });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar usuários'
      )
    );
  }
};

/**
 * Fetches all users with pagination support (simplified version for backward compatibility).
 * Admin access required.
 *
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @param search - Optional search term
 * @param includeInactive - Whether to include inactive users
 * @param userGroupId - Optional user group ID to filter users
 * @returns Promise with paginated array of users
 */
export const getAllUsersPaginated = async (
  page: number = 1,
  limit: number = 100,
  search?: string,
  includeInactive: boolean = false,
  userGroupId?: number,
  signal?: AbortSignal
): Promise<PaginatedUserResponse> => {
  const skip = (page - 1) * limit;
  const params: Record<string, unknown> = {
    skip,
    limit,
    include_inactive: includeInactive
  };

  if (search) {
    params.search = search;
  }

  if (userGroupId !== undefined && userGroupId !== null) {
    params.user_group_id = userGroupId;
  }

  try {
    const response = await axiosInstance.get('/users/all', { params, signal });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar usuários'
      )
    );
  }
};

/**
 * Updates a user's active status.
 * Admin access required.
 * 
 * @param userId - ID of the user to update
 * @param active - New active status
 * @returns Promise with updated user data
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await updateUserStatus(123, false);
 *   console.log('User deactivated:', response.data);
 * } catch (error) {
 *   console.error('Failed to update user status:', error);
 * }
 * ```
 */
export const updateUserStatus = async (userId: number, active: boolean): Promise<UserApiResponse> => {
  try {
    const statusUpdate: UserStatusUpdate = { active };
    const response = await axiosInstance.patch(`/users/${userId}/status`, statusUpdate);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar status do usuário'
      )
    );
  }
};

/**
 * Gets token usage information for a specific user.
 * Admin access required.
 * 
 * @param userId - ID of the user to check
 * @returns Promise with token usage data
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await getUserTokenUsage(123);
 *   console.log('Token usage:', response.data.usage_percentage + '%');
 * } catch (error) {
 *   console.error('Failed to fetch token usage:', error);
 * }
 * ```
 */
export const getUserTokenUsage = async (userId: number): Promise<TokenUsageApiResponse> => {
  try {
    const response = await axiosInstance.get(`/users/tokens/${userId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar uso de tokens do usuário'
      )
    );
  }
};

/**
 * Creates a new user account.
 * Admin access required.
 * 
 * @param userData - User creation data
 * @returns Promise with created user data
 * 
 * @example
 * ```typescript
 * try {
 *   const newUser = await createUser({
 *     email: 'user@example.com',
 *     password: 'securepassword123',
 *     name: 'John Doe',
 *     phone: '(48) 99999-9999'
 *   });
 *   console.log('User created:', newUser.data);
 * } catch (error) {
 *   console.error('Failed to create user:', error);
 * }
 * ```
 */
export const createUser = async (userData: UserCreate): Promise<UserApiResponse> => {
  try {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar usuário'
      )
    );
  }
};

/**
 * Updates user token limit (admin only).
 *
 * @param userId - ID of the user
 * @param tokenLimit - New token limit (null for unlimited)
 * @returns Promise with updated user data
 */
export const updateUserTokenLimit = async (userId: number, tokenLimit: number | null): Promise<UserApiResponse> => {
  try {
    const response = await axiosInstance.patch(`/users/${userId}/token-limit`, { token_limit: tokenLimit });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar limite de tokens'
      )
    );
  }
};

/**
 * Resets user token counter (admin only).
 *
 * @param userId - ID of the user
 * @returns Promise with reset confirmation
 */
export const resetUserTokens = async (userId: number): Promise<UserApiResponse> => {
  try {
    const response = await axiosInstance.post(`/users/${userId}/reset-tokens`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao resetar tokens do usuário'
      )
    );
  }
};

